import type { IAccordionBuild, IAccordionConfig, IAccordionItemConfig } from './types';

import { merge } from '../../helper';

import addClass from '../../general/class/addClass';

import * as exElement from '../../utils/element';

import * as exButton from '../button';

// ----------------------------------------------------------------------------

function create(config: IAccordionConfig = {}): IAccordionBuild {
    // Default Config
    const defConfig: IAccordionConfig = {};

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    const src: IAccordionBuild = {
        element: document.createElement('div'),
        list: [],
    };

    // Create element (accordion)
    const elAccordion = exElement.create({
        merge: defConfig,
        class: {
            new: 'accordion',
        },
    });
    src.element = elAccordion;

    // ------------------------------------------------------------------------

    // If there is no data
    if (!defConfig.data?.length) {
        return src;
    }

    // Foreach Data
    for (let i = 0; i < defConfig.data.length; i++) {
        const item = defConfig.data[i];

        // Default Config Item
        const defConfigItem: IAccordionItemConfig = {
            attr: {
                id: item.id ?? 'accordion' + (i + 1),
            },
        };

        // Merge
        merge(defConfigItem, item);
        const itemId = defConfigItem.attr?.id ?? 'accordion' + (i + 1);

        // --------------------------------------------------------------------

        // Create element (accordion item)
        const elAccordionItem = exElement.create({
            merge: defConfig.item,
            class: {
                new: 'accordion-item',
            },
        });
        elAccordion.appendChild(elAccordionItem);

        // --------------------------------------------------------------------

        // Create element (accordion header)
        const elAccordionHeader = exElement.create({
            merge: defConfig.header,
            class: {
                new: 'accordion-header',
            },
            attr: {
                id: 'accordion-heading' + (i + 1),
            },
        });
        elAccordionItem.appendChild(elAccordionHeader);

        // Create element (accordion button)
        const accordionButton = exButton.create({
            merge: defConfig.button,
            class: {
                new: 'accordion-button collapsed',
            },
            dataset: {
                bsToggle: 'collapse',
                bsTarget: '#' + itemId,
            },
            html: defConfigItem.title,
        });
        elAccordionHeader.appendChild(accordionButton.element);

        // --------------------------------------------------------------------

        // Create element (accordion collapse)
        const elAccordionCollapse = exElement.create({
            merge: defConfig.collapse,
            class: {
                new: 'accordion-collapse collapse',
            },
            attr: {
                id: itemId,
            },
        });
        elAccordionItem.appendChild(elAccordionCollapse);

        // Create element (accordion body)
        const elAccordionBody = exElement.create({
            merge: defConfig.body,
            class: {
                new: 'accordion-body',
            },
            html: defConfigItem.html,
        });
        elAccordionCollapse.appendChild(elAccordionBody);

        // --------------------------------------------------------------------

        // List Push
        src.list.push({
            element: elAccordionItem,
            header: elAccordionHeader,
            body: elAccordionBody,
        });
    }

    // ------------------------------------------------------------------------

    // Settings
    settings(src, defConfig);

    // ------------------------------------------------------------------------

    // Return
    return src;
}

function settings(elements: IAccordionBuild, config: IAccordionConfig = {}): void {
    // If using flush
    if (config.flush === true) {
        addClass(elements.element, 'accordion-flush');
    }
}

// ----------------------------------------------------------------------------

export { create };
